import { Injectable } from '@angular/core';
import { Brand as MultiLocationBrand, MultiLocationService } from '@vendasta/multi-location';
import {
  filter,
  tap,
  distinctUntilChanged,
  combineLatest,
  switchMap,
  startWith,
  catchError,
  Observable,
  ReplaySubject,
  of,
  map,
  shareReplay,
} from 'rxjs';
import { partnerId } from '../../globals';
import { LocationsService, isBrand } from '../locations';
import { ExecutiveReportSection } from '../reports/executive-report/interface';
import { BrandsApiService, Group } from './brands.api.service';
import { Brand } from './brand';

@Injectable({ providedIn: 'root' })
export class BrandsService {
  public currentBrand$ = this.locationsService.currentLocation$.pipe(
    filter(isBrand),
    tap(() => this.setPath(null)),
    shareReplay(1),
  );

  private path$$ = new ReplaySubject<string[]>(1);
  public path$ = combineLatest([this.currentBrand$, this.path$$]).pipe(
    map(([brand, path]) => (path ? path : brand.groupNodes)),
    distinctUntilChanged((a, b) => a.join('|') === b.join('|')),
    shareReplay(1),
  );

  public allGroups$ = this.currentBrand$.pipe(
    switchMap((brand) => this.brandsApiService.listChildGroups(partnerId, brand?.groupNodes[0] || '')),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  public childGroups$ = this.path$.pipe(
    switchMap((path) => {
      return this.brandsApiService.listChildGroups(partnerId, path.join('|')).pipe(startWith(null as Group[]));
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  public hasChildGroups$ = this.childGroups$.pipe(
    map((childGroups) => {
      if (!childGroups) {
        return undefined;
      } else {
        return childGroups.length > 0;
      }
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  disabledExecutiveReportSections$ = this.currentBrand$.pipe(
    map((brand) => {
      const disabledSections: ExecutiveReportSection[] = [];
      // Check Google My Business Insights
      if (!brand.insightsTabVisible) {
        disabledSections.push(ExecutiveReportSection.GOOGLE_MY_BUSINESS);
      }
      // Check Listings tab
      if (!brand.listingsTabVisible) {
        disabledSections.push(ExecutiveReportSection.LISTINGS);
      }
      // Check Social tab
      if (!brand.socialTabVisible) {
        disabledSections.push(ExecutiveReportSection.SOCIAL);
      }
      // Check Advertising tab
      if (!brand.advertisingTabVisible) {
        disabledSections.push(ExecutiveReportSection.ADVERTISING);
      }
      // Check Website tab
      if (!brand.websiteTabVisible) {
        disabledSections.push(ExecutiveReportSection.WEBSITE);
      }
      // Check Reviews tab
      if (!brand.reviewsTabVisible) {
        disabledSections.push(ExecutiveReportSection.REPUTATION);
      }
      return disabledSections;
    }),
  );

  constructor(
    private locationsService: LocationsService,
    private brandsApiService: BrandsApiService,
    private multiLocationService: MultiLocationService,
  ) {}

  public setPath(path: string[]): void {
    this.path$$.next(path);
  }

  public clearPath(): void {
    this.path$$.next(null);
  }

  getBrand(path: string = null): Observable<Brand> {
    return of(path).pipe(
      switchMap((path) => {
        //path could be either a brand path or a group id.
        if (path.startsWith(`G-`)) {
          return this.multiLocationService.getBrandByGroup(path).pipe(
            //A brand name could potentially start with `G-` so on 404s we can fallback to the brand path
            catchError((err) => {
              if (err?.status == 404) {
                return this.multiLocationService.getBrand(`${partnerId}|${path}`);
              }
              throw err;
            }),
          );
        } else if (path.startsWith(`${partnerId}|`)) {
          return this.multiLocationService.getBrand(path);
        } else {
          return this.multiLocationService.getBrand(`${partnerId}|${path}`);
        }
      }),
      map((brand: MultiLocationBrand) => new Brand(brand)),
    );
  }
}
